import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2Button,
  V2Table,
  V2FormGrid,
  V2FormField,
  ConfirmDialog,
  LoadingState,
  StatePanel,
  Modal,
  Icon,
  type V2TableColumn,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, apiDelete, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { amountColumnHeader, formatRialFa, movementIdByNumber } from '../../lib/finance-display.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import type {
  PurchaseOrderDetail,
  PurchaseOrderLine,
  PurchaseOrderStatus,
  OrderReceiptsResponse,
  PurchaseOrderLandedCostsResponse,
  PurchaseOrderChargeType,
  LandedCostAllocationMethod,
} from '../../lib/types/procurement.js';
import { orderStatusOf } from '../../lib/types/procurement.js';
import type { MovementListResponse } from '../../lib/types/inventoryMovements.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { RelatedOpenItemsCard } from '../finance/RelatedOpenItemsCard.js';
import { ProcurementAttachmentsCard } from './ProcurementAttachmentsCard.js';

const STATUS_TONE: Record<PurchaseOrderStatus, V2BadgeTone> = {
  draft: 'neutral',
  submitted: 'info',
  approved: 'brand',
  in_transit: 'info',
  partially_received: 'warn',
  received: 'success',
  cancelled: 'neutral',
};

type Confirming = 'submit' | 'approve' | 'cancel' | null;

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Procurement.dc.html §02 PURCHASE ORDERS — detail
 *   (receipt + finance summary panel)
 * archetype: ENTITY_DETAIL + approval workflow
 * status: KIT_CONFORMANT — line-level receipt status (ordered/received/
 *   outstanding) is real, from the order detail's own `lines[]`. The
 *   receipts list below it is the real `GET .../orders/:ref/receipts`
 *   (movement number + quantity + timestamp per posted receipt — a
 *   different, genuinely real endpoint from the board's assumption).
 *
 *   BUG-06 reversal: an earlier pass here deliberately left the real
 *   `POST .../orders/:ref/receipts` write endpoint unwired, citing the
 *   design reference's own copy ("ثبت دریافت از این صفحه انجام نمی‌شود؛
 *   رسید انبار در ماژول انبار ثبت می‌شود") as a product-ownership
 *   decision. The aryapakhsh demo QA pass named that exact gap a P0
 *   contract violation instead — a receipt entered only from
 *   `/inventory/receipts`, by free-text order reference, with no picker
 *   against this PO's own lines — so this page now has the real receipt
 *   form, scoped to this order's outstanding line quantities, a
 *   warehouse/location picker (BUG-11), and the same endpoint. The
 *   finance summary card reads GET .../finance/ap/open-items
 *   and shows rows whose sourceReference matches this order ref. A 403
 *   keeps the honest "نیازمند دسترسی مالی" copy. Receipt movement numbers
 *   link to inventory movement detail when GET .../inventory/receipts
 *   can resolve movementId from movementNumber.
 * state reference: ready · permission_denied · not_found · network_error
 */
export function OrderDetailPage() {
  const { unit, label } = useAmountDisplay();
  const { slug, ref } = useParams<{ slug: string; ref: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptBusinessDate, setReceiptBusinessDate] = useState('');
  const [receiptWarehouseCode, setReceiptWarehouseCode] = useState('');
  const [receiptLocationCode, setReceiptLocationCode] = useState('');
  const [receiptQuantities, setReceiptQuantities] = useState<Record<number, string>>({});
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [receiptSubmitting, setReceiptSubmitting] = useState(false);

  // In-Transit tracking modal
  const [transitModalOpen, setTransitModalOpen] = useState(false);
  const [carrierName, setCarrierName] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippedAt, setShippedAt] = useState('');
  const [transitSubmitting, setTransitSubmitting] = useState(false);
  const [transitError, setTransitError] = useState<string | null>(null);

  // Landed costs charge modal
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [chargeType, setChargeType] = useState<PurchaseOrderChargeType>('freight');
  const [chargeTitle, setChargeTitle] = useState('');
  const [chargeAmountRial, setChargeAmountRial] = useState('');
  const [chargeSupplierPartyRef, setChargeSupplierPartyRef] = useState('');
  const [chargeAllocationMethod, setChargeAllocationMethod] =
    useState<LandedCostAllocationMethod>('value');
  const [chargeNotes, setChargeNotes] = useState('');
  const [chargeSubmitting, setChargeSubmitting] = useState(false);
  const [chargeError, setChargeError] = useState<string | null>(null);

  const order = useApiGet<PurchaseOrderDetail>(
    slug && ref ? `${base}/api/v1/procurement/orders/${ref}` : null,
    [slug, ref, refreshKey],
  );
  const receipts = useApiGet<OrderReceiptsResponse>(
    slug && ref ? `${base}/api/v1/procurement/orders/${ref}/receipts` : null,
    [slug, ref, refreshKey],
  );
  const landedCosts = useApiGet<PurchaseOrderLandedCostsResponse>(
    slug && ref ? `${base}/api/v1/procurement/orders/${ref}/landed-costs` : null,
    [slug, ref, refreshKey],
  );
  const parties = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/parties?limit=200` : null,
    [slug],
  );
  const inventoryReceipts = useApiGet<MovementListResponse>(
    slug ? `${base}/api/v1/inventory/receipts` : null,
    [slug],
  );
  const warehouses = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/warehouses?status=active&limit=200` : null,
    [slug],
  );
  const locations = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/locations?status=active&limit=200` : null,
    [slug],
  );

  if (!slug || !ref) return null;

  const orderStatus = order.status === 'ready' ? orderStatusOf(order.data.statusLabel) : undefined;
  const canReceive =
    orderStatus === 'approved' ||
    orderStatus === 'in_transit' ||
    orderStatus === 'partially_received';

  const landedData = landedCosts.status === 'ready' ? landedCosts.data : null;

  const outstandingLines =
    order.status === 'ready'
      ? order.data.lines.filter((l) => Number(l.quantity) - Number(l.receivedQuantity) > 1e-9)
      : [];
  const warehouseOptions = warehouses.status === 'ready' ? warehouses.data.items : [];
  const locationOptions = locations.status === 'ready' ? locations.data.items : [];
  const warehouseIdByCode = new Map(
    warehouseOptions.map((w) => [String(w['code']), String(w['warehouseId'])]),
  );
  const receiptLocationOptions = (() => {
    const warehouseId = warehouseIdByCode.get(receiptWarehouseCode);
    if (!warehouseId) return [];
    return locationOptions.filter((l) => String(l['warehouseId']) === warehouseId);
  })();

  function openReceiptForm() {
    const defaults: Record<number, string> = {};
    for (const line of outstandingLines) {
      defaults[line.rowNumber] = String(Number(line.quantity) - Number(line.receivedQuantity));
    }
    setReceiptQuantities(defaults);
    setReceiptBusinessDate('');
    setReceiptWarehouseCode('');
    setReceiptLocationCode('');
    setReceiptError(null);
    setReceiptOpen(true);
  }

  async function submitReceipt() {
    setReceiptError(null);
    if (!receiptBusinessDate || !receiptWarehouseCode || !receiptLocationCode) {
      setReceiptError('انبار، محل انبار و تاریخ سند الزامی است.');
      return;
    }
    const lines = outstandingLines
      .map((line) => ({
        rowNumber: line.rowNumber,
        quantity: (receiptQuantities[line.rowNumber] ?? '').trim(),
      }))
      .filter((line) => Number(line.quantity) > 0);
    if (lines.length === 0) {
      setReceiptError('حداقل یک قلم با مقدار بیشتر از صفر لازم است.');
      return;
    }
    for (const line of lines) {
      const orderLine = outstandingLines.find((l) => l.rowNumber === line.rowNumber)!;
      const outstanding = Number(orderLine.quantity) - Number(orderLine.receivedQuantity);
      if (Number(line.quantity) > outstanding + 1e-9) {
        setReceiptError(`مقدار رسید ردیف ${line.rowNumber} از مقدار باقی‌مانده بیشتر است.`);
        return;
      }
    }
    setReceiptSubmitting(true);
    try {
      await apiPost(
        `${base}/api/v1/procurement/orders/${ref}/receipts`,
        {
          warehouseRef: receiptWarehouseCode,
          warehouseLocationRef: receiptLocationCode,
          businessDate: receiptBusinessDate,
          lines,
        },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setReceiptOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setReceiptError(
        err instanceof ApiError ? err.envelope.error.message : 'ثبت رسید ناموفق بود.',
      );
    } finally {
      setReceiptSubmitting(false);
    }
  }

  async function runAction(action: 'submit' | 'approve' | 'cancel') {
    setSubmitting(true);
    setError(null);
    try {
      await apiPost(`${base}/api/v1/procurement/orders/${ref}/${action}`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.envelope.error.message : 'ثبت اقدام ناموفق بود.');
    } finally {
      setSubmitting(false);
      setConfirming(null);
    }
  }

  async function submitTransit() {
    setTransitError(null);
    setTransitSubmitting(true);
    try {
      await apiPost(`${base}/api/v1/procurement/orders/${ref}/in-transit`, {
        carrierName: carrierName.trim() || undefined,
        trackingNumber: trackingNumber.trim() || undefined,
        shippedAt: shippedAt.trim() || undefined,
      });
      setTransitModalOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setTransitError(
        err instanceof ApiError ? err.envelope.error.message : 'ثبت وضعیت در راه ناموفق بود.',
      );
    } finally {
      setTransitSubmitting(false);
    }
  }

  async function submitCharge() {
    setChargeError(null);
    if (!chargeTitle.trim() || !chargeAmountRial.trim()) {
      setChargeError('عنوان و مبلغ هزینه الزامی است.');
      return;
    }
    setChargeSubmitting(true);
    try {
      await apiPost(`${base}/api/v1/procurement/orders/${ref}/charges`, {
        chargeType,
        title: chargeTitle.trim(),
        amountRial: chargeAmountRial.trim(),
        supplierPartyRef: chargeSupplierPartyRef.trim() || undefined,
        allocationMethod: chargeAllocationMethod,
        notes: chargeNotes.trim() || undefined,
      });
      setChargeModalOpen(false);
      setChargeTitle('');
      setChargeAmountRial('');
      setChargeSupplierPartyRef('');
      setChargeNotes('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setChargeError(
        err instanceof ApiError ? err.envelope.error.message : 'ثبت هزینه ناموفق بود.',
      );
    } finally {
      setChargeSubmitting(false);
    }
  }

  async function handleDeleteCharge(chargeId: string) {
    try {
      await apiDelete(`${base}/api/v1/procurement/orders/${ref}/charges/${chargeId}`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.envelope.error.message : 'حذف هزینه ناموفق بود.');
    }
  }

  const lineColumns: readonly V2TableColumn<PurchaseOrderLine & { id: string }>[] = [
    {
      key: 'product',
      header: 'کالا / خدمت',
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 500 }} className="ltr">
            {row.productRef}
          </span>
          {row.description ? (
            <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>{row.description}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'مقدار سفارش',
      align: 'end',
      render: (row) => <span className="sk-num">{row.quantity}</span>,
    },
    {
      key: 'received',
      header: 'دریافت‌شده',
      align: 'end',
      render: (row) => <span className="sk-num">{row.receivedQuantity}</span>,
    },
    { key: 'uom', header: 'واحد', render: (row) => row.uomRef },
    {
      key: 'unitPrice',
      header: amountColumnHeader('قیمت واحد', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.unitPriceRial, unit)}</span>,
    },
    {
      key: 'lineTotal',
      header: amountColumnHeader('جمع خط', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.lineTotalRial, unit)}</span>,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="sk-breadcrumb">
        <span>تدارکات و خرید</span>
        <span>/</span>
        <Link to={`${base}/procurement/orders`}>سفارش‌های خرید</Link>
        <span>/</span>
        <b className="ltr" style={{ fontFamily: 'var(--sk-font-mono)' }}>
          {ref}
        </b>
      </div>

      {order.status === 'loading' ? <LoadingState /> : null}
      {order.status === 'error' ? (
        <StatePanel
          variant={
            order.error.status === 403
              ? 'permission_denied'
              : order.error.status === 404
                ? 'not_found'
                : 'network_error'
          }
          title={
            order.error.status === 403
              ? 'دسترسی ندارید'
              : order.error.status === 404
                ? 'پیدا نشد'
                : 'قابل نمایش نیست'
          }
        />
      ) : null}

      {order.status === 'ready' ? (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>
                {order.data.supplierPartyRef}
              </h1>
              <V2Badge tone={STATUS_TONE[orderStatus ?? 'draft'] ?? 'neutral'}>
                {order.data.statusLabel}
              </V2Badge>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Link
                to={`${base}/procurement/orders/${ref}/print`}
                className="sk-btn sk-btn--secondary sk-btn--sm"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Icon name="invoice" size="sm" />
                چاپ سفارش خرید
              </Link>
              <div
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}
              >
                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>مبلغ کل ({label})</span>
                <span className="ltr sk-num" style={{ fontSize: 18, fontWeight: 700 }}>
                  {formatRialFa(order.data.totalRial, unit)}
                </span>
              </div>
            </div>
          </div>

          {/* Workflow Stepper: رهگیری فرآیند تحویل */}
          {orderStatus !== 'cancelled'
            ? (() => {
                const steps = [
                  { key: 'draft', label: 'پیش‌نویس' },
                  { key: 'submitted', label: 'ارسال برای تأیید' },
                  { key: 'approved', label: 'تأیید تجاری' },
                  { key: 'in_transit', label: 'ارسال بار / در راه' },
                  { key: 'received', label: 'تحویل و رسید انبار' },
                ];
                const currentStepIdx =
                  orderStatus === 'draft'
                    ? 0
                    : orderStatus === 'submitted'
                      ? 1
                      : orderStatus === 'approved'
                        ? 2
                        : orderStatus === 'in_transit'
                          ? 3
                          : orderStatus === 'partially_received' || orderStatus === 'received'
                            ? 4
                            : -1;

                return (
                  <V2Card>
                    <div
                      style={{
                        padding: '14px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                      }}
                    >
                      {steps.map((s, idx) => {
                        const isPassed = currentStepIdx > idx;
                        const isCurrent = currentStepIdx === idx;
                        return (
                          <div
                            key={s.key}
                            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                          >
                            <div
                              style={{
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 600,
                                background: isPassed
                                  ? 'var(--sk-success)'
                                  : isCurrent
                                    ? 'var(--sk-primary)'
                                    : 'var(--sk-bg-subtle)',
                                color: isPassed || isCurrent ? '#fff' : 'var(--sk-fg-muted)',
                              }}
                            >
                              {isPassed ? <Icon name="check" size="sm" /> : idx + 1}
                            </div>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: isCurrent ? 600 : 400,
                                color: isCurrent ? 'var(--sk-fg)' : 'var(--sk-fg-muted)',
                              }}
                            >
                              {s.label}
                            </span>
                            {idx < steps.length - 1 ? (
                              <span style={{ margin: '0 6px', color: 'var(--sk-border)' }}>←</span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </V2Card>
                );
              })()
            : null}

          <V2Card>
            <div
              style={{
                padding: '16px 20px',
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 16,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>شماره</span>
                <span
                  className="ltr sk-num"
                  style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--sk-font-mono)' }}
                >
                  {order.data.ref}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>درخواست مرتبط</span>
                <span className="ltr sk-num" style={{ fontSize: 14, fontWeight: 500 }}>
                  {order.data.requestRef ? (
                    <Link to={`${base}/procurement/requests/${order.data.requestRef}`}>
                      {order.data.requestRef}
                    </Link>
                  ) : (
                    '—'
                  )}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>پروژه مرتبط</span>
                <span className="ltr sk-num" style={{ fontSize: 14, fontWeight: 500 }}>
                  {order.data.projectRef ? (
                    <Link to={`${base}/projects/list/${order.data.projectRef}`}>
                      {order.data.projectRef}
                    </Link>
                  ) : (
                    '—'
                  )}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>تاریخ سفارش</span>
                <span className="sk-num" style={{ fontSize: 14, fontWeight: 500 }}>
                  {order.data.orderDate}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>موعد تحویل</span>
                <span className="sk-num" style={{ fontSize: 14, fontWeight: 500 }}>
                  {order.data.expectedDate ?? '—'}
                </span>
              </div>
              {order.data.carrierName || order.data.trackingNumber || order.data.shippedAt ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                      شرکت حمل / باربری
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>
                      {order.data.carrierName ?? '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                      شماره بارنامه / رهگیری
                    </span>
                    <span
                      className="ltr sk-num"
                      style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--sk-font-mono)' }}
                    >
                      {order.data.trackingNumber ?? '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                      تاریخ ارسال بار
                    </span>
                    <span className="sk-num" style={{ fontSize: 14, fontWeight: 500 }}>
                      {order.data.shippedAt ?? '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>وضعیت حمل</span>
                    <span style={{ fontSize: 13, color: 'var(--sk-primary)', fontWeight: 500 }}>
                      {orderStatus === 'in_transit'
                        ? 'بار در مسیر ارسال به انبار'
                        : 'ارسال ثبت‌شده'}
                    </span>
                  </div>
                </>
              ) : null}
              {order.data.notes ? (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    borderTop: '1px solid var(--sk-divider, #e5e7eb)',
                    paddingTop: 10,
                  }}
                >
                  <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>یادداشت سفارش</span>
                  <span style={{ fontSize: 13 }}>{order.data.notes}</span>
                </div>
              ) : null}
            </div>
          </V2Card>

          <V2Card>
            <V2CardHeader
              title="اقلام سفارش"
              actions={
                <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                  {order.data.lines.length} قلم
                </span>
              }
            />
            <V2Table
              columns={lineColumns}
              rows={order.data.lines.map((l) => ({ ...l, id: String(l.rowNumber) }))}
              zebra
            />
          </V2Card>

          {/* Landed Costs: هزینه‌های جانبی و بهای تمام‌شده ورودی انبار */}
          <V2Card>
            <V2CardHeader
              title="هزینه‌های سربار و بهای تمام‌شده ورودی انبار (Landed Costs)"
              actions={
                orderStatus !== 'cancelled' ? (
                  <V2Button
                    variant="secondary"
                    size="sm"
                    icon="plus"
                    onClick={() => {
                      setChargeError(null);
                      setChargeTitle('');
                      setChargeAmountRial('');
                      setChargeSupplierPartyRef('');
                      setChargeNotes('');
                      setChargeModalOpen(true);
                    }}
                  >
                    ثبت هزینه سربار
                  </V2Button>
                ) : null
              }
            />

            {/* Summary statistics */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12,
                padding: '12px 20px',
                background: 'var(--sk-bg-subtle)',
                borderBottom: '1px solid var(--sk-divider)',
              }}
            >
              <div>
                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)', display: 'block' }}>
                  مبلغ پایه سفارش
                </span>
                <span className="ltr sk-num" style={{ fontSize: 14, fontWeight: 600 }}>
                  {formatRialFa(order.data.totalRial, unit)} {label}
                </span>
              </div>
              <div>
                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)', display: 'block' }}>
                  مجموع هزینه‌های سربار (کرایه، بیمه، ...)
                </span>
                <span
                  className="ltr sk-num"
                  style={{ fontSize: 14, fontWeight: 600, color: 'var(--sk-primary)' }}
                >
                  {formatRialFa(landedData?.totalChargesRial ?? '0', unit)} {label}
                </span>
              </div>
              <div>
                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)', display: 'block' }}>
                  بهای تمام‌شده کل ورودی انبار
                </span>
                <span
                  className="ltr sk-num"
                  style={{ fontSize: 15, fontWeight: 700, color: 'var(--sk-success)' }}
                >
                  {formatRialFa(landedData?.grandTotalRial ?? order.data.totalRial, unit)} {label}
                </span>
              </div>
            </div>

            {/* Registered Charges Table */}
            {landedData && landedData.charges.length > 0 ? (
              <div style={{ padding: '12px 20px' }}>
                <span style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  هزینه‌های جانبی ثبت‌شده ({landedData.charges.length})
                </span>
                <div className="sk-table-scroll">
                  <table className="sk-table sk-table--compact">
                    <thead>
                      <tr>
                        <th>نوع هزینه</th>
                        <th>عنوان</th>
                        <th>طرف حساب / باربری</th>
                        <th>روش تسهیم</th>
                        <th style={{ textAlign: 'end' }}>مبلغ ({label})</th>
                        <th>یادداشت</th>
                        <th style={{ width: 44 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {landedData.charges.map((c) => (
                        <tr key={c.chargeId}>
                          <td>
                            <V2Badge tone="neutral">{c.chargeTypeLabel}</V2Badge>
                          </td>
                          <td style={{ fontWeight: 500 }}>{c.title}</td>
                          <td>{c.supplierPartyName ?? c.supplierPartyRef ?? '—'}</td>
                          <td>
                            <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                              {c.allocationMethodLabel}
                            </span>
                          </td>
                          <td style={{ textAlign: 'end' }} className="ltr sk-num">
                            {formatRialFa(c.amountRial, unit)}
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                            {c.notes || '—'}
                          </td>
                          <td>
                            {orderStatus !== 'cancelled' ? (
                              <button
                                type="button"
                                className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
                                style={{ color: 'var(--sk-danger)' }}
                                title="حذف هزینه"
                                onClick={() => void handleDeleteCharge(c.chargeId)}
                              >
                                <Icon name="x" size="sm" />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                هنوز هزینه جانبی (کرایه حمل، بیمه، ترخیص و ...) برای این سفارش ثبت نشده است.
              </div>
            )}

            {/* Landed Cost Allocation per Line */}
            {landedData && landedData.lines.length > 0 && landedData.charges.length > 0 ? (
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--sk-divider)' }}>
                <span style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'block' }}>
                  تسهیم هزینه سربار و بهای تمام‌شده واحد اقلام جهت ورود به انبار
                </span>
                <div className="sk-table-scroll">
                  <table className="sk-table sk-table--compact">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>کالا</th>
                        <th style={{ textAlign: 'end' }}>مقدار</th>
                        <th style={{ textAlign: 'end' }}>بهای خرید واحد</th>
                        <th style={{ textAlign: 'end' }}>بهای کل خرید</th>
                        <th style={{ textAlign: 'end' }}>سهم هزینه سربار</th>
                        <th style={{ textAlign: 'end' }}>بهای تمام‌شده کل</th>
                        <th style={{ textAlign: 'end', color: 'var(--sk-primary)' }}>
                          فی تمام‌شده ورودی انبار ({label})
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {landedData.lines.map((l) => (
                        <tr key={l.rowNumber}>
                          <td className="sk-num">{l.rowNumber}</td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span className="ltr" style={{ fontWeight: 500 }}>
                                {l.productRef}
                              </span>
                              {l.description ? (
                                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>
                                  {l.description}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td style={{ textAlign: 'end' }} className="sk-num">
                            {l.quantity}
                          </td>
                          <td style={{ textAlign: 'end' }} className="ltr sk-num">
                            {formatRialFa(l.unitPriceRial, unit)}
                          </td>
                          <td style={{ textAlign: 'end' }} className="ltr sk-num">
                            {formatRialFa(l.lineTotalRial, unit)}
                          </td>
                          <td
                            style={{ textAlign: 'end', color: 'var(--sk-primary)' }}
                            className="ltr sk-num"
                          >
                            +{formatRialFa(l.allocatedChargesRial, unit)}
                          </td>
                          <td style={{ textAlign: 'end', fontWeight: 600 }} className="ltr sk-num">
                            {formatRialFa(l.landedTotalRial, unit)}
                          </td>
                          <td
                            style={{
                              textAlign: 'end',
                              fontWeight: 700,
                              color: 'var(--sk-primary)',
                            }}
                            className="ltr sk-num"
                          >
                            {formatRialFa(l.landedUnitCostRial, unit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </V2Card>

          <V2Card>
            <V2CardHeader
              title="رسیدهای ثبت‌شده"
              actions={
                canReceive && !receiptOpen ? (
                  <V2Button variant="primary" size="sm" icon="plus" onClick={openReceiptForm}>
                    ثبت رسید
                  </V2Button>
                ) : !canReceive ? (
                  <span className="sk-tag">فقط خواندنی</span>
                ) : null
              }
            />
            {receipts.status === 'loading' ? <LoadingState /> : null}
            {receipts.status === 'error' ? (
              <div
                style={{
                  padding: '16px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                  {receipts.error.status === 403
                    ? 'ثبت و مشاهدهٔ عملیات انبار نیازمند دسترسی انبار است.'
                    : 'رسیدها قابل نمایش نیست.'}
                </span>
              </div>
            ) : null}
            {receipts.status === 'ready' ? (
              receipts.data.receipts.length === 0 ? (
                <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                  هنوز رسیدی برای این سفارش ثبت نشده.
                </div>
              ) : (
                <div className="sk-table-scroll">
                  <table className="sk-table sk-table--compact">
                    <thead>
                      <tr>
                        <th style={{ width: 150 }}>شمارهٔ سند انبار</th>
                        <th style={{ width: 80 }}>ردیف</th>
                        <th style={{ width: 100 }}>مقدار</th>
                        <th style={{ width: 130 }}>تاریخ دریافت</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipts.data.receipts.map((r, i) => {
                        const movementId =
                          inventoryReceipts.status === 'ready'
                            ? movementIdByNumber(inventoryReceipts.data.movements, r.movementNumber)
                            : null;
                        return (
                          <tr key={i}>
                            <td
                              className="ltr"
                              style={{ fontFamily: 'var(--sk-font-mono)', fontSize: 13 }}
                            >
                              {movementId ? (
                                <Link to={`${base}/inventory/movements/${movementId}`}>
                                  {r.movementNumber}
                                </Link>
                              ) : (
                                r.movementNumber
                              )}
                            </td>
                            <td className="sk-num">{r.orderLineRowNumber}</td>
                            <td className="sk-num">{r.quantity}</td>
                            <td className="sk-num">{r.receivedAt.slice(0, 10)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ) : null}

            {receiptOpen ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  padding: '16px 20px',
                  borderTop: '1px solid var(--sk-divider)',
                }}
              >
                <V2FormGrid>
                  <V2FormField label="انبار" required width="medium">
                    <select
                      className="sk-select"
                      value={receiptWarehouseCode}
                      onChange={(e) => {
                        setReceiptWarehouseCode(e.target.value);
                        setReceiptLocationCode('');
                      }}
                    >
                      <option value="">— انتخاب انبار —</option>
                      {warehouseOptions.map((w) => (
                        <option key={String(w['code'])} value={String(w['code'])}>
                          {String(w['name'] ?? w['code'])}
                        </option>
                      ))}
                    </select>
                  </V2FormField>
                  <V2FormField label="محل انبار" required width="medium">
                    <select
                      className="sk-select"
                      value={receiptLocationCode}
                      disabled={!receiptWarehouseCode}
                      title={!receiptWarehouseCode ? 'ابتدا انبار را انتخاب کنید' : undefined}
                      onChange={(e) => setReceiptLocationCode(e.target.value)}
                    >
                      <option value="">
                        {!receiptWarehouseCode ? 'ابتدا انبار را انتخاب کنید' : '— انتخاب محل —'}
                      </option>
                      {receiptLocationOptions.map((l) => (
                        <option key={String(l['code'])} value={String(l['code'])}>
                          {String(l['name'] ?? l['code'])}
                        </option>
                      ))}
                    </select>
                  </V2FormField>
                  <V2FormField label="تاریخ رسید" required width="medium">
                    <JalaliDateInput
                      value={receiptBusinessDate}
                      onChange={setReceiptBusinessDate}
                    />
                  </V2FormField>
                </V2FormGrid>

                <div className="sk-table-scroll">
                  <table className="sk-table sk-table--compact">
                    <thead>
                      <tr>
                        <th>کالا</th>
                        <th style={{ width: 100 }}>باقی‌مانده</th>
                        <th style={{ width: 120 }}>مقدار رسید</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outstandingLines.map((line) => {
                        const outstanding = Number(line.quantity) - Number(line.receivedQuantity);
                        return (
                          <tr key={line.rowNumber}>
                            <td className="ltr">{line.productRef}</td>
                            <td className="sk-num">{outstanding}</td>
                            <td>
                              <input
                                className="sk-input ltr sk-num"
                                style={{ width: '100%' }}
                                value={receiptQuantities[line.rowNumber] ?? ''}
                                onChange={(e) =>
                                  setReceiptQuantities((prev) => ({
                                    ...prev,
                                    [line.rowNumber]: e.target.value,
                                  }))
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {receiptError ? (
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: 'var(--sk-r-md)',
                      background: 'var(--sk-danger-soft)',
                      fontSize: 13,
                      color: 'var(--sk-fg)',
                    }}
                  >
                    {receiptError}
                  </div>
                ) : null}

                <div style={{ display: 'flex', gap: 8 }}>
                  <V2Button
                    variant="primary"
                    disabled={receiptSubmitting}
                    onClick={() => void submitReceipt()}
                  >
                    {receiptSubmitting ? 'در حال ثبت…' : 'ثبت رسید'}
                  </V2Button>
                  <V2Button
                    variant="ghost"
                    disabled={receiptSubmitting}
                    onClick={() => setReceiptOpen(false)}
                  >
                    انصراف
                  </V2Button>
                </div>
              </div>
            ) : null}
          </V2Card>

          <RelatedOpenItemsCard kind="ap" sourceReference={order.data.ref} base={base} />

          <ProcurementAttachmentsCard
            base={base}
            targetType="order"
            targetRef={ref}
            readOnly={orderStatus === 'cancelled'}
          />

          {error ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '13px 16px',
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-danger-soft)',
                border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
              }}
            >
              <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>{error}</span>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 8 }}>
            {orderStatus === 'draft' ? (
              <>
                <V2Button
                  variant="primary"
                  disabled={submitting}
                  onClick={() => setConfirming('submit')}
                >
                  ارسال برای تأیید
                </V2Button>
                <V2Button
                  variant="ghost"
                  style={{ color: 'var(--sk-danger)' }}
                  disabled={submitting}
                  onClick={() => setConfirming('cancel')}
                >
                  حذف پیش‌نویس
                </V2Button>
              </>
            ) : null}
            {orderStatus === 'submitted' ? (
              <>
                <V2Button
                  variant="primary"
                  disabled={submitting}
                  onClick={() => setConfirming('approve')}
                >
                  تأیید سفارش
                </V2Button>
                <V2Button
                  variant="ghost"
                  disabled={submitting}
                  onClick={() => setConfirming('cancel')}
                >
                  لغو سفارش
                </V2Button>
              </>
            ) : null}
            {orderStatus === 'approved' ? (
              <>
                <V2Button
                  variant="primary"
                  disabled={submitting}
                  onClick={() => {
                    setTransitError(null);
                    setCarrierName(order.data.carrierName ?? '');
                    setTrackingNumber(order.data.trackingNumber ?? '');
                    setShippedAt(order.data.shippedAt ?? '');
                    setTransitModalOpen(true);
                  }}
                >
                  ثبت ارسال بار (در راه)
                </V2Button>
                <V2Button
                  variant="ghost"
                  disabled={submitting}
                  onClick={() => setConfirming('cancel')}
                >
                  لغو سفارش
                </V2Button>
              </>
            ) : null}
            {orderStatus === 'in_transit' ? (
              <>
                {canReceive && !receiptOpen ? (
                  <V2Button variant="primary" onClick={openReceiptForm}>
                    ثبت رسید انبار
                  </V2Button>
                ) : null}
                <V2Button
                  variant="ghost"
                  disabled={submitting}
                  onClick={() => setConfirming('cancel')}
                >
                  لغو سفارش
                </V2Button>
              </>
            ) : null}
          </div>
        </>
      ) : null}

      {/* In-Transit Tracking Modal */}
      <Modal
        open={transitModalOpen}
        title="ثبت ارسال بار و تغییر وضعیت به «در راه»"
        onClose={() => setTransitModalOpen(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <V2Button
              variant="ghost"
              disabled={transitSubmitting}
              onClick={() => setTransitModalOpen(false)}
            >
              انصراف
            </V2Button>
            <V2Button
              variant="primary"
              disabled={transitSubmitting}
              onClick={() => void submitTransit()}
            >
              {transitSubmitting ? 'در حال ثبت…' : 'تأیید و ارسال بار'}
            </V2Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--sk-fg-muted)', margin: 0 }}>
            با ثبت این مرحله، وضعیت سفارش به <b>«در راه»</b> تغییر یافته و انبار آماده دریافت بار
            خواهد شد.
          </p>

          <V2FormGrid>
            <V2FormField label="شرکت حمل‌کننده / باربری">
              <input
                className="sk-input"
                placeholder="مثال: تیپاکس، چاپار، باربری وطن"
                value={carrierName}
                onChange={(e) => setCarrierName(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="شماره بارنامه / کد رهگیری">
              <input
                className="sk-input ltr"
                placeholder="مثال: 987654321"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="تاریخ ارسال بار">
              <JalaliDateInput value={shippedAt} onChange={setShippedAt} />
            </V2FormField>
          </V2FormGrid>

          {transitError ? (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-danger-soft)',
                fontSize: 13,
                color: 'var(--sk-danger)',
              }}
            >
              {transitError}
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Add Landed Cost Charge Modal */}
      <Modal
        open={chargeModalOpen}
        title="ثبت هزینه جانبی و سربار خرید (Landed Cost)"
        onClose={() => setChargeModalOpen(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <V2Button
              variant="ghost"
              disabled={chargeSubmitting}
              onClick={() => setChargeModalOpen(false)}
            >
              انصراف
            </V2Button>
            <V2Button
              variant="primary"
              disabled={chargeSubmitting}
              onClick={() => void submitCharge()}
            >
              {chargeSubmitting ? 'در حال ثبت…' : 'ثبت و تسهیم هزینه'}
            </V2Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <V2FormGrid>
            <V2FormField label="نوع هزینه" required>
              <select
                className="sk-select"
                value={chargeType}
                onChange={(e) => setChargeType(e.target.value as PurchaseOrderChargeType)}
              >
                <option value="freight">کرایه حمل</option>
                <option value="insurance">بیمه باربری</option>
                <option value="customs">عوارض و گمرک</option>
                <option value="handling">تخلیه و بارگیری</option>
                <option value="other">سایر هزینه‌ها</option>
              </select>
            </V2FormField>
            <V2FormField label="عنوان هزینه" required>
              <input
                className="sk-input"
                placeholder="مثال: کرایه حمل از انبار تأمین‌کننده به کارخانه"
                value={chargeTitle}
                onChange={(e) => setChargeTitle(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="مبلغ (ریال)" required>
              <input
                className="sk-input ltr sk-num"
                placeholder="مبلغ به ریال"
                value={chargeAmountRial}
                onChange={(e) => setChargeAmountRial(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="طرف حساب / پیمانکار حمل">
              <select
                className="sk-select"
                value={chargeSupplierPartyRef}
                onChange={(e) => setChargeSupplierPartyRef(e.target.value)}
              >
                <option value="">— انتخاب طرف حساب (اختیاری) —</option>
                {(parties.status === 'ready' ? parties.data.items : []).map((p) => (
                  <option key={String(p.code)} value={String(p.code)}>
                    {String(p.name ?? p.code)}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="روش تسهیم بین اقلام" required>
              <select
                className="sk-select"
                value={chargeAllocationMethod}
                onChange={(e) =>
                  setChargeAllocationMethod(e.target.value as LandedCostAllocationMethod)
                }
              >
                <option value="value">بر اساس ارزش ریالی اقلام (پیش‌فرض استاندارد)</option>
                <option value="quantity">بر اساس تعداد / مقدار اقلام</option>
                <option value="equal">تسهیم مساوی بین اقلام</option>
              </select>
            </V2FormField>
            <V2FormField label="یادداشت">
              <input
                className="sk-input"
                placeholder="توضیحات و شماره فاکتور هزینه"
                value={chargeNotes}
                onChange={(e) => setChargeNotes(e.target.value)}
              />
            </V2FormField>
          </V2FormGrid>

          {chargeError ? (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-danger-soft)',
                fontSize: 13,
                color: 'var(--sk-danger)',
              }}
            >
              {chargeError}
            </div>
          ) : null}
        </div>
      </Modal>

      {confirming === 'submit' ? (
        <ConfirmDialog
          open
          title="ارسال سفارش برای تأیید؟"
          message="پس از ارسال، سفارش برای تأیید در دسترس قرار می‌گیرد."
          confirmLabel={submitting ? 'در حال ثبت…' : 'ارسال'}
          cancelLabel="انصراف"
          onConfirm={() => void runAction('submit')}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
      {confirming === 'approve' ? (
        <ConfirmDialog
          open
          title="تأیید سفارش خرید؟"
          message="پس از تأیید، سفارش آمادهٔ دریافت کالا در انبار می‌شود."
          confirmLabel={submitting ? 'در حال ثبت…' : 'تأیید'}
          cancelLabel="انصراف"
          onConfirm={() => void runAction('approve')}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
      {confirming === 'cancel' ? (
        <ConfirmDialog
          open
          title="لغو سفارش خرید؟"
          message="این اقدام برگشت‌پذیر نیست."
          danger
          confirmLabel={submitting ? 'در حال ثبت…' : 'لغو سفارش'}
          cancelLabel="انصراف"
          onConfirm={() => void runAction('cancel')}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
    </div>
  );
}
